/**
 * 我的
 * ============================================================
 * 对应需求文档 V1.0 的 E05 个人中心，但**只做本地数据部分**。
 *
 * 为什么没有账号体系：
 *   文档里的 E05 包含账户、等级、订阅、用量 —— 那些需要后端用户系统
 *   与支付，属于另一个阶段。本版本所有数据都在本机（localStorage），
 *   所以这一页如实呈现"本机数据"，并提供清理与导出入口，
 *   而不是摆一个登录按钮却登不上去。
 *
 * 文档第 5 节原则：「透明告知 —— 对音频保存与使用方式明确说明」。
 * 所以这里也把"数据存在哪、怎么删"写清楚。
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getProgress, getCompletedLessons, resetProgress } from '../lib/courseProgress';
import { getReviewStats, clearReviewQueue } from '../lib/reviewQueue';
import { getSelectedDialectId, fetchDialect, STATUS_STYLE } from '../lib/dialects';
import { readCached, fetchQuota, describeStatus } from '../lib/license';
import { getUpdateState, subscribeUpdate, refreshUpdate } from '../lib/update';
import { siteUrl, assetUrl } from '../lib/assetUrl';
import BottomNav from '../components/BottomNav';
import SiteFooter from '../components/SiteFooter';

export default function ProfilePage() {
  const [dialect, setDialect] = useState(null);
  const [completed, setCompleted] = useState(() => getCompletedLessons());
  const [review, setReview] = useState(() => getReviewStats());
  const [progress, setProgress] = useState(() => getProgress(0));
  const [confirming, setConfirming] = useState(null);
  /*
   * 授权状态。
   *
   * 先用本地缓存渲染（避免首屏闪一下空白），再拉服务端的最新值。
   * 服务端没启用授权时（本地开发 / 自用）返回 enabled:false，
   * 此时整张卡片都不渲染 —— 不要给用户看一个"永久 0 点"的框。
   */
  const [ent, setEnt] = useState(() => readCached());
  const [copied, setCopied] = useState(false);

  // 版本信息：单例状态 + 订阅，首屏用缓存渲染，随后拉最新
  const [upd, setUpd] = useState(() => getUpdateState());
  useEffect(() => {
    refreshUpdate();
    return subscribeUpdate(setUpd);
  }, []);

  useEffect(() => {
    const id = getSelectedDialectId();
    fetchDialect(id)
      .then(setDialect)
      .catch(() => {});
    fetchQuota()
      .then(setEnt)
      .catch(() => {
        /* 拿不到就沿用缓存；再拿不到就不显示卡片，不影响其他功能 */
      });
  }, []);

  const inviteText = describeStatus(ent);

  const refresh = () => {
    setCompleted(getCompletedLessons());
    setReview(getReviewStats());
    setProgress(getProgress(0));
    setConfirming(null);
  };

  const doReset = () => {
    resetProgress();
    refresh();
  };

  const doClearReview = () => {
    clearReviewQueue();
    refresh();
  };

  return (
    <div className="min-h-screen bg-[#fdfaf1] pb-28 dark:bg-zinc-900">
      <header className="bg-gradient-to-br from-teal-500 to-sky-500 px-6 pb-6 pt-12 dark:from-teal-700 dark:to-sky-700">
        <h1 className="text-2xl font-bold text-white">我的</h1>
        <p className="mt-1 text-sm text-white/80">
          {dialect ? `当前方言：${dialect.displayName}` : '数据都保存在这台设备上'}
        </p>
      </header>

      <main className="px-5 py-5 space-y-4">
        {/* 学习概览 */}
        <section className="rounded-2xl bg-white p-4 dark:bg-zinc-800">
          <p className="mb-3 text-xs font-bold text-zinc-500">学习概览</p>
          <div className="grid grid-cols-3 gap-2 text-center">
            <Stat value={completed.length} label="已完成课" />
            <Stat value={review.total || 0} label="待复习" />
            <Stat value={`${progress?.percent ?? 0}%`} label="本方言进度" />
          </div>
        </section>

        {/* 当前方言能力 */}
        {dialect && (
          <section className="rounded-2xl bg-white p-4 dark:bg-zinc-800">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs font-bold text-zinc-500">当前方言能力</p>
              <Link to="/dialects" className="text-xs text-primary">更换</Link>
            </div>
            <div className="space-y-1.5">
              {(dialect.badges || []).map((b) => {
                const st = STATUS_STYLE[b.id] || STATUS_STYLE.unavailable;
                return (
                  <div key={b.key} className="flex items-center justify-between">
                    <span className="text-xs text-zinc-600 dark:text-zinc-300">{b.label}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${st.cls}`}>
                      {st.label}
                    </span>
                  </div>
                );
              })}
            </div>
            {dialect.review?.reason && (
              <p className="mt-2 border-t border-zinc-100 pt-2 text-[10px] leading-relaxed text-zinc-400 dark:border-zinc-700">
                试用状态：{dialect.review.verdictLabel} · {dialect.review.reason}
              </p>
            )}
          </section>
        )}

        {/* 授权与额度 —— 只在服务端启用了授权时显示 */}
        {inviteText && (
          <section className="rounded-2xl bg-white p-4 dark:bg-zinc-800">
            <p className="mb-2 text-xs font-bold text-zinc-500">授权与额度</p>
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p
                  className={`text-sm font-bold ${
                    inviteText.tone === 'ok'
                      ? 'text-teal-600 dark:text-teal-400'
                      : inviteText.tone === 'expired'
                        ? 'text-amber-600 dark:text-amber-400'
                        : 'text-zinc-800 dark:text-zinc-100'
                  }`}
                >
                  {inviteText.title}
                </p>
                <p className="mt-0.5 text-[11px] leading-relaxed text-zinc-500 dark:text-zinc-400">
                  {inviteText.detail}
                </p>
              </div>
              {/*
               * 卡密入口在所有「服务端启用了授权」的状态下都显示：
               *   · 试用中  —— 想提前升级成月卡/年卡
               *   · 已激活  —— 想续期或换更高档
               *   · 已到期  —— 恢复使用
               * 之前只在 expired 时显示，导致试用期/已激活时找不到入口。
               * 统一触发全局卡密弹层（MANUAL），与被 402 拦下时同一套。
               */}
              <button
                type="button"
                onClick={() => {
                  window.dispatchEvent(
                    new CustomEvent('dg:quota-blocked', {
                      detail: { code: 'MANUAL', message: '' },
                    })
                  );
                }}
                className="shrink-0 rounded-full bg-teal-500 px-3 py-1.5 text-[11px] font-bold text-white"
              >
                输入卡密
              </button>
            </div>
            <p className="mt-2.5 text-[10px] leading-relaxed text-zinc-400">
              额度每天重置。浏览课程和老师口型片段不消耗额度。
            </p>
          </section>
        )}

        {/* 版本信息 —— 只有壳版本落后时才提示重装，网页层自动更新不打扰 */}
        {upd.loaded && (
          <section className="rounded-2xl bg-white p-4 dark:bg-zinc-800">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-bold text-zinc-500">版本信息</p>
              {upd.hasUpdate && (
                <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-600 dark:bg-rose-900/40 dark:text-rose-300">
                  有新版本
                </span>
              )}
            </div>

            <div className="space-y-1.5 text-[12px] text-zinc-600 dark:text-zinc-300">
              <div className="flex items-center justify-between">
                <span>当前版本</span>
                <span className="font-bold">
                  {upd.current ? `v${upd.current}` : '网页版'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>最新版本</span>
                <span className="font-bold text-teal-600 dark:text-teal-400">
                  v{upd.latest || upd.webVersion || '—'}
                </span>
              </div>
              {upd.releasedAt && (
                <div className="flex items-center justify-between">
                  <span>发布日期</span>
                  <span>{upd.releasedAt}</span>
                </div>
              )}
            </div>

            {/* 更新说明 */}
            {upd.notes && upd.notes.length > 0 && (
              <ul className="mt-2.5 space-y-1 border-t border-zinc-100 pt-2.5 text-[11px] leading-relaxed text-zinc-400 dark:border-zinc-700">
                {upd.notes.map((n, i) => (
                  <li key={i}>· {n}</li>
                ))}
              </ul>
            )}

            {/* 有更新才给"去更新"入口 */}
            {upd.hasUpdate && (
              <button
                type="button"
                onClick={() => {
                  // 跳到下载页拿新 APK（下载页会读 release.json 给最新链接）
                  window.location.href = siteUrl('/download/');
                }}
                className="mt-3 w-full rounded-xl bg-teal-500 py-2.5 text-sm font-bold text-white active:opacity-70"
              >
                去更新（v{upd.latest}）
              </button>
            )}

            {/* 网页版访问时给个"下载 App"的温和引导（不是强推） */}
            {!upd.current && !upd.hasUpdate && (
              <p className="mt-2.5 border-t border-zinc-100 pt-2.5 text-[11px] leading-relaxed text-zinc-400 dark:border-zinc-700">
                你正在用网页版，页面与内容会自动更新，无需操作。需要 App 可到
                <a href={assetUrl('/download/')} className="text-teal-500 underline">
                  下载页
                </a>
                。
              </p>
            )}
          </section>
        )}

        {/* 分享下载 —— 用户彼此推荐的主要入口 */}
        <section className="rounded-2xl bg-white p-4 dark:bg-zinc-800">
          <p className="mb-2 text-xs font-bold text-zinc-500">推荐给朋友</p>
          <button
            type="button"
            onClick={async () => {
              const url = siteUrl('/download/');
              try {
                if (navigator.share) {
                  await navigator.share({ title: '方言通 · DialectGo', url });
                  return;
                }
                await navigator.clipboard.writeText(url);
                setCopied(true);
                setTimeout(() => setCopied(false), 1600);
              } catch {
                /* 用户取消分享时不提示错误 */
              }
            }}
            className="w-full rounded-xl bg-teal-50 py-2.5 text-xs font-bold text-teal-700 active:opacity-70 dark:bg-teal-900/30 dark:text-teal-300"
          >
            {copied ? '链接已复制' : '分享下载链接'}
          </button>
          <a
            href={assetUrl('/download/')}
            target="_blank"
            rel="noreferrer"
            className="mt-2 block text-center text-[10px] text-zinc-400 underline"
          >
            打开下载页（含安装说明）
          </a>
        </section>

        {/* 数据说明 —— 文档要求「透明告知」 */}
        <section className="rounded-2xl bg-white p-4 dark:bg-zinc-800">
          <p className="mb-2 text-xs font-bold text-zinc-500">关于你的数据</p>
          <ul className="space-y-1.5 text-[11px] leading-relaxed text-zinc-500 dark:text-zinc-400">
            <li>· 学习进度、复习队列、当前方言都存在**这台设备的浏览器**里，不上传服务器。</li>
            <li>· 换设备或清理浏览器数据会丢失，没有云端同步。</li>
            <li>· 录音只用于当次语音识别，识别完即丢弃，不落盘保存。</li>
          </ul>
        </section>

        {/* 清理操作 */}
        <section className="rounded-2xl bg-white p-4 dark:bg-zinc-800">
          <p className="mb-3 text-xs font-bold text-zinc-500">清理</p>

          <Danger
            label="清空复习队列"
            desc={`当前 ${review.total || 0} 项`}
            confirming={confirming === 'review'}
            onAsk={() => setConfirming('review')}
            onCancel={() => setConfirming(null)}
            onConfirm={doClearReview}
            disabled={!review.total}
          />

          <Danger
            label="重置学习进度"
            desc={`当前已完成 ${completed.length} 课，重置后无法恢复`}
            confirming={confirming === 'progress'}
            onAsk={() => setConfirming('progress')}
            onCancel={() => setConfirming(null)}
            onConfirm={doReset}
            disabled={!completed.length}
          />
        </section>

        <SiteFooter />
      </main>

      <BottomNav />
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <p className="text-xl font-bold text-teal-600 dark:text-teal-400">{value}</p>
      <p className="mt-0.5 text-[11px] text-zinc-400">{label}</p>
    </div>
  );
}

/**
 * 危险操作按钮 —— 二次确认内联展开，不用 window.confirm
 * （移动端弹窗体验差，且 confirm 在部分 WebView 里被禁用）
 */
function Danger({ label, desc, confirming, onAsk, onCancel, onConfirm, disabled }) {
  return (
    <div className="border-b border-zinc-100 py-2.5 last:border-0 dark:border-zinc-700">
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-zinc-700 dark:text-zinc-200">{label}</p>
          <p className="text-[11px] text-zinc-400">{desc}</p>
        </div>
        {!confirming && (
          <button
            type="button"
            onClick={onAsk}
            disabled={disabled}
            className="shrink-0 rounded-full border border-rose-200 px-3 py-1 text-xs font-bold text-rose-600 disabled:opacity-30 dark:border-rose-800 dark:text-rose-400"
          >
            清理
          </button>
        )}
      </div>
      {confirming && (
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 rounded-full bg-rose-500 py-1.5 text-xs font-bold text-white"
          >
            确认清除
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-full bg-zinc-100 py-1.5 text-xs font-bold text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300"
          >
            取消
          </button>
        </div>
      )}
    </div>
  );
}
